import type { Room, Booking, BookingStatus, StaffMember } from '@/types/booking';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Building2, StickyNote, CalendarIcon } from 'lucide-react';
import { ToggleWithLabel } from '@/components/ui/toggle-with-label';
import { TimeSelect } from '@/components/ui/time-select';
import { useStaff } from '@/contexts/StaffContext';
import { staffConfig } from '@/config/staff';
import { useToast } from '@/hooks/use-toast';
import { useBookings } from '@/contexts/BookingContext';
import { validateBookingTime } from '@/lib/booking-utils';
import { ConflictWarningDialog } from './ConflictWarningDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDevice } from '@/hooks/use-device';

type QuickBookingProps = {
  rooms: Room[];
  staffMembers: StaffMember[];
  onBookingCreate: (booking: Booking) => void;
  onClose: () => void;
  onBookingComplete: (date: Date) => void;
};

const statusStyles = {
  paid: 'px-3 py-1.5 w-[200px] bg-blue-100 border-l-4 border-blue-500 text-blue-800 hover:bg-blue-200/80 transition-all duration-200',
  booked: 'px-3 py-1.5 w-[200px] bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 hover:bg-emerald-200/80 transition-all duration-200',
  pending: 'px-3 py-1.5 w-[200px] bg-amber-100 border-l-4 border-amber-500 text-amber-800 hover:bg-amber-200/80 transition-all duration-200'
};

export function QuickBooking({ 
  rooms, 
  staffMembers, 
  onBookingCreate, 
  onClose,
  onBookingComplete 
}: QuickBookingProps) {
  const { isMobile } = useDevice();
  const { selectedStaff } = useStaff();
  const { checkDuplicate } = useBookings();
  const { toast } = useToast();
  const [open, setOpen] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictBooking, setConflictBooking] = useState<Booking | null>(null);
  const [pendingBookings, setPendingBookings] = useState<Booking[]>([]);

  const [form, setForm] = useState({
    isHavenBooking: false,
    roomId: rooms[0]?.id || 0,
    clientName: '',
    bookedBy: selectedStaff?.name || '',
    date: new Date(),
    timeSlot: '09:00',
    duration: '1',
    status: 'pending' as BookingStatus,
    amount: selectedStaff ? staffConfig.find(s => s.name === selectedStaff.name)?.defaults.amount || 0 : 0,
    isFullDay: false,
    hasNotes: false,
    notes: ''
  });

  const validateAndCreateBooking = async (booking: Booking) => {
    // Validate booking time
    const timeError = validateBookingTime(booking);
    if (timeError) {
      throw new Error(timeError);
    }

    // Check for conflicts
    const conflict = checkDuplicate(booking);
    if (conflict) {
      throw { type: 'conflict', conflict };
    }

    await onBookingCreate(booking);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const roomsToBook = form.isHavenBooking ? rooms : [rooms.find(r => r.id === form.roomId)!];
      const newBookings: Booking[] = [];
      
      for (const room of roomsToBook) {
        const newBooking: Booking = {
          id: Date.now() + room.id,
          roomId: room.id,
          date: format(form.date, 'yyyy-MM-dd'),
          timeSlot: form.isFullDay ? '09:00' : form.timeSlot,
          duration: form.isFullDay ? 14 : parseInt(form.duration),
          bookedBy: form.bookedBy,
          clientName: form.bookedBy === 'SuHe' ? 'SuHe' : form.clientName,
          status: form.status,
          revenue: {
            amount: form.bookedBy === 'SuHe' ? 0 : form.amount,
            hasNotes: form.hasNotes,
            notes: form.hasNotes ? form.notes : undefined
          }
        };

        try {
          await validateAndCreateBooking(newBooking);
          newBookings.push(newBooking);
        } catch (error: any) {
          if (error.type === 'conflict') {
            setConflictBooking(newBooking);
            setPendingBookings([...newBookings, newBooking]);
            setShowConflictWarning(true);
            return;
          }
          throw error;
        }
      }

      toast({
        title: "Success",
        description: form.isHavenBooking
          ? `Created Haven booking for all ${rooms.length} rooms`
          : `Created booking for ${form.bookedBy === 'SuHe' ? 'SuHe' : form.clientName}`,
      });

      onBookingComplete(form.date);
      handleClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create booking",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAlternativeSelect = async (newDate: string, newTime: string) => {
    if (!conflictBooking || !pendingBookings.length) return;

    setIsSubmitting(true);
    const failedBookings: Booking[] = [];
    const successfulBookings: Booking[] = [];

    try {
      for (const booking of pendingBookings) {
        const updatedBooking = {
          ...booking,
          date: newDate,
          timeSlot: newTime
        };

        try {
          await validateAndCreateBooking(updatedBooking);
          successfulBookings.push(updatedBooking);
        } catch (error) {
          failedBookings.push(booking);
          if (failedBookings.length === pendingBookings.length) {
            // All bookings failed
            throw new Error("Could not find a suitable time slot for the booking(s)");
          }
        }
      }

      if (failedBookings.length > 0) {
        toast({
          title: "Partial Success",
          description: `Created ${successfulBookings.length} bookings, ${failedBookings.length} failed`,
          variant: "warning"
        });
      } else {
        toast({
          title: "Success",
          description: `Created ${successfulBookings.length} bookings at the new time`,
        });
      }

      onBookingComplete(parseISO(newDate));
      handleClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create bookings",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleDurationChange = (value: string) => {
    if (value === 'full') {
      setForm(prev => ({ 
        ...prev, 
        duration: '14', 
        isFullDay: true,
        timeSlot: '09:00' // Always set to 9:00 for full day bookings
      }));
    } else {
      setForm(prev => ({ 
        ...prev, 
        duration: value, 
        isFullDay: false 
      }));
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setForm(prev => ({ ...prev, date }));
      setCalendarOpen(false);
    }
  };

  const handleStaffSelect = (value: string) => {
    const staffDefaults = staffConfig.find(s => s.name === value)?.defaults;
    setForm(prev => ({
      ...prev,
      bookedBy: value,
      clientName: value === 'SuHe' ? 'SuHe' : '',
      amount: 0,
      status: staffDefaults?.status || 'pending'
    }));
  };

  const isSuHeSelected = form.bookedBy === 'SuHe';
  const isFormValid = form.bookedBy && (isSuHeSelected || form.clientName);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className={cn(
          "max-w-2xl",
          isMobile && "w-[95vw] p-4 max-h-[95vh]"
        )}>
          <DialogHeader>
            <DialogTitle>Quick Booking</DialogTitle>
          </DialogHeader>

          <ScrollArea className={cn(
            "pr-4 -mr-4",
            isMobile ? "max-h-[calc(95vh-8rem)]" : "max-h-[calc(85vh-8rem)]"
          )}>
            <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <ToggleWithLabel
                  id="haven-booking"
                  checked={form.isHavenBooking}
                  onChange={(checked) => 
                    setForm(prev => ({
                      ...prev,
                      isHavenBooking: checked
                    }))
                  }
                  label="Haven Booking"
                  leftIcon={<Building2 className="h-4 w-4" />}
                />
              </div>

              {form.isHavenBooking ? (
                <div className={cn(
                  "bg-purple-50 rounded-lg border border-purple-100",
                  isMobile ? "p-3" : "p-4"
                )}>
                  <div className="flex items-center gap-2 text-purple-800">
                    <Building2 className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Haven Booking Mode</p>
                      <p className="text-sm text-purple-700">
                        This booking will reserve all {rooms.length} rooms simultaneously
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="room">Room</Label>
                  <Select
                    value={form.roomId.toString()}
                    onValueChange={(value) => setForm(prev => ({ ...prev, roomId: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map(room => (
                        <SelectItem key={room.id} value={room.id.toString()}>
                          {room.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="bookedBy">Booked By</Label>
                <Select 
                  value={form.bookedBy}
                  onValueChange={handleStaffSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffConfig.map(staff => (
                      <SelectItem key={staff.id} value={staff.name}>
                        <span className={cn("px-2 py-1 rounded-md", staff.color)}>
                          {staff.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientName">Client Company</Label>
                <Input
                  id="clientName"
                  value={isSuHeSelected ? 'SuHe' : form.clientName}
                  onChange={(e) => setForm(prev => ({ ...prev, clientName: e.target.value }))}
                  disabled={isSuHeSelected}
                  placeholder={form.bookedBy ? "Enter client name" : "Select staff member first"}
                  className={cn(
                    "hover:border-gray-400 focus:border-purple-500 transition-colors",
                    isSuHeSelected && "bg-gray-50"
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label>Date & Time</Label>
                <div className={cn(
                  "flex gap-2",
                  isMobile && "flex-col"
                )}>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          isMobile ? "w-full" : "w-[280px]",
                          !form.date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.date ? format(form.date, 'PPP') : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <TimeSelect 
                    value={form.timeSlot}
                    onChange={(value) => setForm(prev => ({ ...prev, timeSlot: value }))}
                    className={isMobile ? "w-full" : undefined}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Select 
                  value={form.isFullDay ? 'full' : form.duration}
                  onValueChange={handleDurationChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full" className="font-medium text-purple-600">
                      Full Day (9:00-23:00)
                    </SelectItem>
                    <SelectItem value="1">1 hour</SelectItem>
                    {Array.from({ length: 7 }, (_, i) => (
                      <SelectItem key={i + 2} value={(i + 2).toString()}>
                        {i + 2} hours
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={form.status}
                  onValueChange={(value) => setForm({...form, status: value as BookingStatus})}
                  disabled={isSuHeSelected}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">
                      <div className={cn("px-3 py-1 rounded-md", statusStyles.paid)}>
                        Paid
                      </div>
                    </SelectItem>
                    <SelectItem value="booked">
                      <div className={cn("px-3 py-1 rounded-md", statusStyles.booked)}>
                        Booked
                      </div>
                    </SelectItem>
                    <SelectItem value="pending">
                      <div className={cn("px-3 py-1 rounded-md", statusStyles.pending)}>
                        Pending
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (€)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    €
                  </span>
                  <Input
                    id="amount"
                    type="text"
                    inputMode="decimal"
                    pattern="^\d*\.?\d{0,2}$"
                    className="pl-7"
                    value={isSuHeSelected ? '0' : form.amount.toString()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = value.split('.');
                      if (parts.length > 2) return;
                      if (parts[1]?.length > 2) return;
                      
                      setForm(prev => ({
                        ...prev,
                        amount: value === '' ? 0 : parseFloat(value) || 0
                      }));
                    }}
                    disabled={isSuHeSelected}
                    style={{ appearance: 'textfield' }}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <ToggleWithLabel
                  id="notes-enabled"
                  checked={form.hasNotes}
                  onChange={(checked) => 
                    setForm(prev => ({
                      ...prev,
                      hasNotes: checked
                    }))
                  }
                  label="Add Notes"
                  leftIcon={<StickyNote className="h-4 w-4" />}
                />

                {form.hasNotes && (
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Add any notes..."
                      value={form.notes}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        notes: e.target.value
                      }))}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {isMobile && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className={cn( 
                  "transition-all duration-200",
                  isMobile && "flex-1",
                  isFormValid
                    ? "bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                )}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Creating...' : form.isHavenBooking
                  ? `Create Haven Booking (${rooms.length} rooms)`
                  : 'Create Booking'
                }
              </Button>
            </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {showConflictWarning && conflictBooking && (
        <ConflictWarningDialog
          conflict={checkDuplicate(conflictBooking)!}
          rooms={[rooms.find(r => r.id === conflictBooking.roomId)!]}
          open={showConflictWarning}
          onClose={() => {
            setShowConflictWarning(false);
            setConflictBooking(null);
            setPendingBookings([]);
            setIsSubmitting(false);
          }}
          onSelectAlternative={handleAlternativeSelect}
        />
      )}
    </>
  );
}